import React, { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';

interface AudioHistoryItem {
  apiKey: string;
  timestamp: string;
  audioUrl: string;
  textPreview?: string;
  bookTitle?: string;
  currentPage?: number;
  totalPages?: number;
  voiceId?: string;
  engine?: string;
  language?: string;
  s3Key?: string;
}

interface AudioHistoryProps {
  apiKey: string;
  onSelectAudio: (audioUrl: string, timestamp: string, textPreview?: string) => void;
  refreshTrigger?: number;
  isLoggedIn: boolean;
}

const client = generateClient({ authMode: 'userPool' });

const GET_AUDIO_HISTORY_QUERY = `
  query GetAudioHistory($apiKey: String!, $limit: Int) {
    getAudioHistory(apiKey: $apiKey, limit: $limit) {
      apiKey
      timestamp
      audioUrl
      textPreview
      bookTitle
      currentPage
      totalPages
      voiceId
      engine
      language
      s3Key
    }
  }
`;

const GET_FRESH_AUDIO_URL_QUERY = `
  query GetFreshAudioUrl($s3Key: String!) {
    getFreshAudioUrl(s3Key: $s3Key)
  }
`;

const AudioHistory: React.FC<AudioHistoryProps> = ({ apiKey, onSelectAudio, refreshTrigger, isLoggedIn }) => {
  const [history, setHistory] = useState<AudioHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const session = await fetchAuthSession();
      if (!session?.tokens?.accessToken) {
        throw new Error('No access token found');
      }

      const result: any = await client.graphql({
        query: GET_AUDIO_HISTORY_QUERY,
        variables: { apiKey, limit: 20 },
        authToken: session.tokens.accessToken.toString(),
      });

      const items = result?.data?.getAudioHistory || [];
      setHistory(items);
    } catch (err) {
      console.error('Error fetching audio history:', err);
      setError('Failed to load audio history');
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  useEffect(() => {
    if (apiKey) {
      fetchHistory();
    }
  }, [apiKey, fetchHistory]);

  // Auto-refresh when refreshTrigger changes
  useEffect(() => {
    if (apiKey && refreshTrigger && refreshTrigger > 0) {
      fetchHistory();
    }
  }, [refreshTrigger, apiKey, fetchHistory]);

  const handleSelect = async (item: AudioHistoryItem, index: number) => {
    setSelectedIndex(index);

    // If the item has an s3Key, fetch a fresh pre-signed URL
    if (item.s3Key) {
      try {
        const session = await fetchAuthSession();
        if (!session?.tokens?.accessToken) {
          throw new Error('No access token found');
        }

        const result: any = await client.graphql({
          query: GET_FRESH_AUDIO_URL_QUERY,
          variables: { s3Key: item.s3Key },
          authToken: session.tokens.accessToken.toString(),
        });

        const freshUrl = result?.data?.getFreshAudioUrl;
        if (freshUrl) {
          onSelectAudio(freshUrl, item.timestamp, item.textPreview);
        } else {
          console.error('Failed to get fresh URL, using stored URL');
          onSelectAudio(item.audioUrl, item.timestamp, item.textPreview);
        }
      } catch (err) {
        console.error('Error fetching fresh URL:', err);
        // Fallback to stored URL if fetching fresh URL fails
        onSelectAudio(item.audioUrl, item.timestamp, item.textPreview);
      }
    } else {
      // No s3Key available, use the stored URL (older entries)
      onSelectAudio(item.audioUrl, item.timestamp, item.textPreview);
    }
  };

  // Group history items by book title
  const groupedHistory = useCallback(() => {
    const groups: { [key: string]: AudioHistoryItem[] } = {};

    history.forEach((item) => {
      const bookTitle = item.bookTitle || 'Unknown Book';
      if (!groups[bookTitle]) {
        groups[bookTitle] = [];
      }
      groups[bookTitle].push(item);
    });

    return groups;
  }, [history]);

  const groups = groupedHistory();

  return (
    <div className="audio-history">
      <h3>Audio History</h3>
      <div className="retention-notice">
        <small>Note: Audio files are automatically deleted after 7 days.</small>
      </div>
      <div className="history-content">
        {!isLoggedIn ? (
          <p className="history-status">Sign in to view your audio history</p>
        ) : loading ? (
          <p className="history-status">Loading history...</p>
        ) : error ? (
          <p className="history-status error">{error}</p>
        ) : history.length === 0 ? (
          <p className="history-status">No audio history yet. Send some text from KOReader to get started!</p>
        ) : (
          <div className="grouped-history">
            {Object.entries(groups).map(([bookTitle, items]) => (
              <div key={bookTitle} className="book-group">
                <h4 className="book-group-title">{bookTitle} ({items.length})</h4>
                <ul className="history-list">
                  {items.map((item, index) => {
                    const globalIndex = history.indexOf(item);
                    return (
                      <li
                        key={`${item.timestamp}-${index}`}
                        className={`history-item ${selectedIndex === globalIndex ? 'selected' : ''}`}
                        onClick={() => handleSelect(item, globalIndex)}
                      >
                        <div className="history-info">
                          <span className="history-time">
                            {new Date(item.timestamp).toLocaleString()}
                          </span>
                          {item.currentPage && item.totalPages && (
                            <span className="history-book">
                              Page {item.currentPage}/{item.totalPages}
                            </span>
                          )}
                          {item.voiceId && (
                            <span className="history-voice">
                              {item.voiceId} ({item.engine})
                            </span>
                          )}
                          {item.textPreview && (
                            <span className="history-preview">"{item.textPreview}"</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioHistory;
